/**
 * What an "Always this session" grant actually covers.
 *
 * The main process records the grant as a per-session boolean. That is not
 * enough on its own: **every** query tab is synced into **every** AI session's
 * workspace, so a grant the user gave from a prompt showing one connection and
 * database would otherwise cover tabs bound to every other connection —
 * including production — for the rest of the session.
 *
 * This module is the narrowing half. A request is auto-approved only when main
 * says the session is granted **and** the grant was given for that exact
 * (connection, database). Anything else prompts again.
 *
 * Two deliberate properties:
 *
 * - **Renderer-side and in-memory only**, mirroring the main-process grant it
 *   narrows. Nothing is persisted, so a reload starts from "ask again" — the
 *   safe direction. It is intentionally not a Zustand store: nothing renders
 *   it, and a store is the kind of thing that later grows persistence.
 * - **It can only ever narrow.** A stale entry here cannot approve anything by
 *   itself: main must independently still hold the grant, or the request never
 *   arrives pre-approved in the first place.
 */

export type ExecutionTarget = {
  connectionId?: string;
  database?: string;
};

/** sessionId → the set of targets that session's grant covers. */
const grantsBySession = new Map<string, Set<string>>();

/**
 * A comparable key, or `null` when the target cannot be identified.
 *
 * `null` is never stored and never matches — including against another
 * unidentifiable target. If we cannot say where a query would land, we are in
 * no position to skip asking about it.
 */
function targetKey(target: ExecutionTarget): string | null {
  if (!target.connectionId || !target.database) return null;
  // JSON-encoded pair rather than a delimiter: the encoding is injective, so
  // no combination of a connection id and a database name can collide with a
  // different pair (a raw separator would need a character neither can hold).
  return JSON.stringify([target.connectionId, target.database]);
}

/** Record that this session's grant covers `target`. */
export function rememberSessionGrant(sessionId: string, target: ExecutionTarget): void {
  const key = targetKey(target);
  if (!key) return;

  const existing = grantsBySession.get(sessionId);
  if (existing) {
    existing.add(key);
    return;
  }
  grantsBySession.set(sessionId, new Set([key]));
}

/** Does this session's grant cover `target`? */
export function isGrantedForTarget(sessionId: string, target: ExecutionTarget): boolean {
  const key = targetKey(target);
  if (!key) return false;

  return grantsBySession.get(sessionId)?.has(key) === true;
}

/** Drop everything. Used by tests; also safe to call on sign-out. */
export function resetExecutionGrants(): void {
  grantsBySession.clear();
}
